import { useEffect } from 'react';
import { pathToState, stateToPath, type PageView } from '../utils/routes';
import { type Lang, DEFAULT_LANG } from '../../functions/lib/i18n';

export interface UrlFilterState {
  region: string;
  category: string;
  shorts: boolean;
  view: PageView;
  lang: Lang;
  window?: string;
  channelId?: string;
  compareIds?: [string, string];
}

const SHORTS_KEY = 'youtubemost_shorts';

function getSavedShorts(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SHORTS_KEY) === '1';
}

/**
 * Determine the initial filter state.
 * Priority: URL path > legacy ?region/?category query (back-compat) > home.
 * Shorts is a UI preference kept in localStorage, not in the URL.
 */
export function parseUrlState(): UrlFilterState {
  if (typeof window === 'undefined') return { region: 'ALL', category: 'ALL', shorts: false, view: 'chart', lang: DEFAULT_LANG };

  let route = pathToState(window.location.pathname);

  // Back-compat: old shareable links used /?region=US&category=10
  if ((!route || (route.region === 'ALL' && route.category === 'ALL')) &&
      window.location.search) {
    const params = new URLSearchParams(window.location.search);
    const region = params.get('region')?.toUpperCase();
    const category = params.get('category');
    if (region || category) {
      route = { region: region || 'ALL', category: category || 'ALL', view: 'chart', lang: route?.lang || DEFAULT_LANG };
    }
  }

  return {
    region: route?.region || 'ALL',
    category: route?.category || 'ALL',
    shorts: getSavedShorts(),
    view: route?.view || 'chart',
    lang: route?.lang || DEFAULT_LANG,
    window: route?.window,
    channelId: route?.channelId,
    compareIds: route?.compareIds,
  };
}

/**
 * Sync filter state to the URL path (so each region/category is a real,
 * shareable, indexable URL) and persist the shorts preference.
 */
export function useUrlStateSync(
  state: UrlFilterState,
  _initialState: UrlFilterState,
  onStateChange?: (state: UrlFilterState) => void
) {
  useEffect(() => {
    localStorage.setItem(SHORTS_KEY, state.shorts ? '1' : '0');

    const path = stateToPath(state.region, state.category, state.view, state.lang, state.window, state.channelId, state.compareIds);
    const current = window.location.pathname + window.location.search;
    if (path !== current) {
      window.history.pushState(null, '', path);
    }
  }, [state.region, state.category, state.shorts, state.view, state.lang, state.window, state.channelId, state.compareIds]);

  useEffect(() => {
    const handlePopState = () => {
      if (onStateChange) onStateChange(parseUrlState());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [onStateChange]);
}

/** Copy the current page URL to the clipboard. */
export async function copyCurrentUrl(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(window.location.href);
    return true;
  } catch {
    return false;
  }
}
